import { createSelector } from "@reduxjs/toolkit";

import { ThemeType } from "Model/Configuration/ITheme";
import { getThemeBySetting } from "Utils/Theme";
import { IState } from "../../Model/State/IState";
import { selectShowCloneDialog } from "./Clone";
import { selectShowDeleteDialog } from "./Delete";
import { selectShowExportDialog } from "./Export";
import { selectShowAddressValidationDialog, selectShowDuplicateCheckDialog } from "./Omikron";
import { selectIsEditing } from "./Projects";
import { selectShowIgnoredValuesDialog } from "./Staging";

export function selectActiveProjectId(state: IState): string {
  return state.ui.activeProjectId;
}

export function selectActiveCompanyId(state: IState): string {
  return state.ui.activeCompanyId;
}

export function selectActiveContactId(state: IState): string {
  return state.ui.activeContactId;
}

export function selectSelectedProjectIds(state: IState): string[] {
  return state.ui.selectedIds;
}

export function selectSelectedProjectsCount(state: IState): number {
  return state.ui.selectedIds.length;
}

export function selectIsDetailsOpen(state: IState): boolean {
  return state.ui.isDetailsOpen;
}

export function selectIsStagingOpen(state: IState): boolean {
  return state.ui.isStagingOpen;
}

export function selectIsToolbarIconsOnly(state: IState): boolean {
  return state.ui.isToolbarIconsOnly;
}

export function selectIsUserSettingsOpen(state: IState): boolean {
  return state.ui.isUserSettingsOpen;
}

export function selectIsLoading(state: IState): boolean {
  return state.ui.isLoading;
}

export function selectIsConfigurationReady(state: IState): boolean {
  return state.ui.isConfigurationReady;
}

export function selectIsTranslationReady(state: IState): boolean {
  return state.ui.isTranslationReady;
}

export const selectIsApplicationReady = createSelector(
  selectIsLoading,
  selectIsConfigurationReady,
  selectIsTranslationReady,
  (isLoading, isConfigurationReady, isTranslationReady): boolean => {
    const isApplicationReady = !isLoading && isConfigurationReady && isTranslationReady;
    return isApplicationReady;
  }
);

export function selectLanguage(state: IState): string {
  return state.ui.language;
}

export function selectAutoCompleteSuggestions(state: IState): string[] {
  return state.ui.autoCompleteSuggestions;
}

export function selectUsernames(state: IState): string[] {
  return state.ui.usernames;
}

export function selectLoginUsername(state: IState): string {
  return state.ui.loginUsername;
}

export function selectVersions(state: IState): Record<string, string> {
  return state.ui.versions;
}

export function selectLastOpenedPID(state: IState): string {
  return state.ui.lastOpenedPID;
}

export function selectIsAutoRefresh(state: IState): boolean {
  return state.ui.isAutoRefresh;
}

export function selectRefreshEveryTime(state: IState): number {
  return state.ui.refreshEvery ?? 60;
}

export function selectIsImporting(state: IState): boolean {
  return state.ui.isImporting;
}

export function selectIsFiltering(state: IState): boolean {
  return state.ui.isFiltering;
}

export function selectIsSorting(state: IState): boolean {
  return state.ui.isSorting;
}

export const selectAllowAutoRefresh = createSelector(
  selectIsLoading,
  selectIsAutoRefresh,
  selectIsEditing,
  selectShowExportDialog,
  selectShowCloneDialog,
  selectIsStagingOpen,
  selectShowIgnoredValuesDialog,
  selectShowDeleteDialog,
  selectShowDuplicateCheckDialog,
  selectShowAddressValidationDialog,
  (
    isLoading,
    isAutoRefresh,
    isEditing,
    isExporting,
    isCloning,
    isStagingOpen,
    isIgnoredValuesOpen,
    isDeleting,
    isDuplicateCheckOpen,
    isAddressValidationOpen
  ): boolean => !(
    isLoading
      || isAutoRefresh
      || isEditing
      || isExporting
      || isCloning
      || isStagingOpen
      || isIgnoredValuesOpen
      || isDeleting
      || isDuplicateCheckOpen
      || isAddressValidationOpen
  )
);

export function selectTheme(state: IState): ThemeType {
  return state.ui.theme;
}

export function selectActualTheme(state: IState): ThemeType {
  return getThemeBySetting(selectTheme(state));
}
